import React, { useEffect, useMemo, useRef, useState } from "react";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Paper,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import muiTheme from "../theme/muiTheme";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";
import { limparCpfCnpj, mascararCpfCnpj, tipoDocumento, validarCnpj, validarCpf } from "../utils/documento";
import DocumentoEstrangeiroCampos, { FlagEstrangeiro } from "../components/DocumentoEstrangeiroCampos";
import {
  DOCUMENTO_ESTRANGEIRO_VAZIO,
  camposDocumentoEstrangeiro,
  descreverDocumentoEstrangeiro,
  validarDocumentoEstrangeiro,
} from "../utils/documentoEstrangeiro";

// ==========================================================
// ALTERAÇÕES CONTRATUAIS - CADASTRO DE SÓCIOS (seção 3)
// Sócio: pessoa física (CPF) ou jurídica (CNPJ), inclusive holdings.
// ==========================================================

const API_SOCIOS = "/api/alteracoes-contratuais/socios";

async function chamarApi(caminho = "", opcoes = {}) {
  const tk = localStorage.getItem("token");
  const resposta = await fetch(`${API_SOCIOS}${caminho}`, {
    ...opcoes,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}`, ...(opcoes.headers || {}) },
  });
  const dados = await resposta.json().catch(() => ({}));
  if (!resposta.ok) throw new Error(dados.erro || "Não foi possível concluir a operação.");
  return dados;
}

const formVazio = { documento: "", nomeRazao: "", apelido: "", ativo: true, observacoes: "", tipoPessoaManual: "", ...DOCUMENTO_ESTRANGEIRO_VAZIO };

// CPF (só números) ou CNPJ (numérico ou alfanumérico, letras A-Z nas 12 primeiras posições).
const numeros = (v) => limparCpfCnpj(v);
const maiusculas = (v) => String(v ?? "").toLocaleUpperCase("pt-BR");

const tipoPorDocumento = (v) => (tipoDocumento(v) === "CPF" ? "PF" : tipoDocumento(v) === "CNPJ" ? "PJ" : "");

const formatarDocumento = (v) => mascararCpfCnpj(v);

const cnpjValido = (valor) => validarCnpj(valor);

const cpfValido = (valor) => validarCpf(valor);

const documentoValido = (v) => (tipoPorDocumento(v) === "PF" ? cpfValido(v) : tipoPorDocumento(v) === "PJ" ? cnpjValido(v) : false);
// Tipo de pessoa: pelo CPF/CNPJ; estrangeiro sem CPF/CNPJ escolhe manualmente.
const tipoDoForm = (f) => tipoPorDocumento(f.documento) || (f.estrangeiro ? f.tipoPessoaManual : "");
const labelTipo = (t) => (t === "PF" ? "Pessoa Física" : t === "PJ" ? "Pessoa Jurídica" : "—");

const normalizar = (texto) =>
  String(texto ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

// Pesquisa com "%" separando termos (ex.: "serbom%ativa").
const corresponde = (valor, termo) =>
  normalizar(termo)
    .split("%")
    .map((t) => t.trim())
    .filter(Boolean)
    .every((t) => normalizar(valor).includes(t));

const estiloCabecalho = {
  backgroundColor: "var(--ig-f-1e1e1e)",
  color: "var(--ig-t-ffffff)",
  fontWeight: "bold",
  borderBottom: "1px solid var(--ig-b-444444)",
  whiteSpace: "nowrap",
};

const estiloBotaoAcao = (cor, corHover) => ({
  backgroundColor: cor,
  color: "#fff",
  borderRadius: "4px",
  mx: 0.25,
  width: "auto",
  "&:hover": { backgroundColor: corHover },
});

const estiloCampo = (somenteLeitura, erro) => ({
  "& .MuiInputLabel-root": { color: erro ? "#f44336" : "var(--ig-t-ffffff)", fontWeight: "bold" },
  "& .MuiOutlinedInput-root": {
    backgroundColor: somenteLeitura ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
    color: "var(--ig-t-ffffff)",
    "& fieldset": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : erro ? "#f44336" : "var(--ig-b-444444)" },
    "&:hover fieldset": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : erro ? "#f44336" : "var(--ig-b-888888)" },
  },
  "& input, & textarea": { color: "var(--ig-t-ffffff)" },
  "& .MuiFormHelperText-root": { color: erro ? "#f44336" : "var(--ig-t-9ca3af)" },
});

const estiloSelect = (somenteLeitura) => ({
  backgroundColor: somenteLeitura ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
  color: "var(--ig-t-ffffff)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : "var(--ig-b-444444)" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : "var(--ig-b-888888)" },
  "& .MuiSvgIcon-root": { color: somenteLeitura ? "transparent" : "var(--ig-t-ffffff)" },
});

export default function SocietarioSocios() {
  const [socios, setSocios] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [processando, setProcessando] = useState(false);

  const [inputFiltro, setInputFiltro] = useState("");
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(10);
  const [gridMinimizado, setGridMinimizado] = useState(false);

  const cnpjInputRef = useRef(null);

  // form inferior: null = novo, valor = id selecionado
  const [editandoId, setEditandoId] = useState(null);
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  const [form, setForm] = useState({ ...formVazio });
  const [errosForm, setErrosForm] = useState({});

  // Consulta de CNPJ (BrasilAPI)
  const [consultandoCnpj, setConsultandoCnpj] = useState(false);
  const [mensagemCnpj, setMensagemCnpj] = useState("");
  const versaoConsultaRef = useRef(0);

  const [confirmDelete, setConfirmDelete] = useState({ aberto: false, id: null });

  const [snack, setSnack] = useState({ aberto: false, msg: "", severity: "success" });
  const mostrarSnack = (msg, severity = "success") => setSnack({ aberto: true, msg, severity });

  // permissões (níveis do sistema)
  const token = localStorage.getItem("token");
  let tipoUsuario = "";
  if (token) {
    try {
      tipoUsuario = normalizar(jwtDecode(token).tipo);
    } catch (_) {}
  }
  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["administrador"].includes(tipoUsuario);

  const carregarSocios = async () => {
    setCarregando(true);
    try {
      const lista = await chamarApi();
      setSocios(Array.isArray(lista) ? lista : []);
    } catch (erro) {
      mostrarSnack(erro.message, "error");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (podeVisualizar) carregarSocios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeVisualizar]);

  // ── Pesquisa automática
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltro(inputFiltro);
      setPagina(0);
    }, 400);
    return () => clearTimeout(t);
  }, [inputFiltro]);

  const sociosOrdenados = useMemo(() => [...socios].sort((a, b) => String(a.nomeRazao).localeCompare(String(b.nomeRazao), "pt-BR")), [socios]);

  const sociosFiltrados = useMemo(() => {
    if (!filtro.trim()) return sociosOrdenados;
    return sociosOrdenados.filter((e) =>
      [
        e.nomeRazao,
        e.apelido,
        e.documento,
        e.documentoExibicao,
        numeros(e.documento),
        labelTipo(e.tipoPessoa),
        e.tipoPessoa,
        e.ativo ? "ativo" : "inativo",
      ].some((v) => corresponde(v, filtro)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sociosOrdenados, filtro]);

  const sociosPagina = sociosFiltrados.slice(pagina * porPagina, pagina * porPagina + porPagina);

  const handlePesquisar = () => {
    setFiltro(inputFiltro);
    setPagina(0);
  };
  const handleLimparFiltro = () => {
    setInputFiltro("");
    setFiltro("");
    setPagina(0);
  };

  const handleCopiar = async () => {
    const texto = sociosFiltrados.map((e) => `${e.nomeRazao} - ${e.documentoExibicao || e.documento}`).join(", ");
    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }
    const ok = await copiarTexto(texto);
    alert(ok ? "Copiado!" : "Não foi possível copiar.");
  };

  const handleCSV = () => {
    const BOM = "﻿";
    const header = ["Tipo", "Nome / Razão Social", "CPF / CNPJ", "Apelido", "Situação", "Observações"];
    const csvEscape = (value) => {
      const texto = String(value ?? "");
      return /[;"\n\r]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
    };
    const linhas = sociosFiltrados.map((e) =>
      [labelTipo(e.tipoPessoa), e.nomeRazao, e.documentoExibicao || e.documento, e.apelido, e.ativo ? "Ativo" : "Inativo", e.observacoes].map(csvEscape),
    );
    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "socios-alteracoes-contratuais.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const limparConsultaCnpj = () => {
    versaoConsultaRef.current += 1;
    setConsultandoCnpj(false);
    setMensagemCnpj("");
  };

  // ── Visualizar (preenche form inferior)
  const handleVisualizar = (socio) => {
    limparConsultaCnpj();
    setEditandoId(socio.id);
    setSomenteLeitura(true);
    setForm({
      documento: formatarDocumento(socio.documento),
      tipoPessoaManual: socio.tipoPessoa || "",
      estrangeiro: Boolean(socio.estrangeiro),
      documentoEstrangeiroTipo: socio.documentoEstrangeiroTipo || "",
      documentoEstrangeiroNumero: socio.documentoEstrangeiroNumero || "",
      documentoEstrangeiroPais: socio.documentoEstrangeiroPais || "",
      nomeRazao: socio.nomeRazao || "",
      apelido: socio.apelido || "",
      ativo: Boolean(socio.ativo),
      observacoes: socio.observacoes || "",
    });
    setErrosForm({});
    setGridMinimizado(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const handleHabilitarEdicao = () => {
    setSomenteLeitura(false);
    setTimeout(() => cnpjInputRef.current?.focus(), 50);
  };

  const handleCancelarForm = () => {
    limparConsultaCnpj();
    setEditandoId(null);
    setSomenteLeitura(false);
    setForm({ ...formVazio });
    setErrosForm({});
    setGridMinimizado(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const atualizarCampo = (campo, valor) => {
    if (somenteLeitura) return;
    setForm((p) => ({ ...p, [campo]: valor }));
    if (errosForm[campo]) setErrosForm((p) => ({ ...p, [campo]: undefined }));
  };

  // Consulta a Razão Social pelo CNPJ ao completar os 14 dígitos.
  const consultarCnpj = async (cnpjDigitado) => {
    const cnpj = numeros(cnpjDigitado);
    if (cnpj.length !== 14) return;
    if (!cnpjValido(cnpj)) {
      setMensagemCnpj("");
      setErrosForm((p) => ({ ...p, documento: "CNPJ inválido. Confira os dígitos verificadores." }));
      return;
    }

    const versao = ++versaoConsultaRef.current;
    setConsultandoCnpj(true);
    setMensagemCnpj("Consultando CNPJ...");

    try {
      const resposta = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
      if (!resposta.ok) throw new Error(resposta.status === 404 ? "CNPJ não encontrado na consulta pública. Informe a Razão Social." : "");
      const dados = await resposta.json();
      if (versao !== versaoConsultaRef.current) return;

      setForm((p) =>
        numeros(p.documento) !== cnpj
          ? p
          : {
              ...p,
              nomeRazao: maiusculas(dados.razao_social || p.nomeRazao),
              apelido: p.apelido || maiusculas(dados.nome_fantasia || ""),
            },
      );
      setErrosForm((p) => ({ ...p, nomeRazao: undefined }));
      setMensagemCnpj("Dados cadastrais localizados.");
    } catch (erro) {
      if (versao === versaoConsultaRef.current) {
        setMensagemCnpj(erro instanceof TypeError || !erro.message ? "Consulta de CNPJ indisponível no momento. Informe a Razão Social." : erro.message);
      }
    } finally {
      if (versao === versaoConsultaRef.current) setConsultandoCnpj(false);
    }
  };

  const validarForm = () => {
    const erros = {};
    const tipo = tipoDoForm(form);
    const informouDocumento = numeros(form.documento).length > 0;
    if (form.estrangeiro) {
      // Estrangeiro: CPF/CNPJ opcional (validado se informado) e documento estrangeiro obrigatório.
      Object.assign(erros, validarDocumentoEstrangeiro(form, { temDocumentoNacional: informouDocumento }));
      if (informouDocumento && !tipoPorDocumento(form.documento)) erros.documento = "Informe um CPF (11 dígitos) ou CNPJ (14 caracteres), ou deixe em branco.";
      else if (informouDocumento && !documentoValido(form.documento))
        erros.documento = tipoPorDocumento(form.documento) === "PF" ? "CPF inválido." : "CNPJ inválido.";
      if (!tipo) erros.tipoPessoaManual = "Informe o tipo de pessoa.";
    } else if (!tipo) erros.documento = "Informe um CPF (11 dígitos) ou CNPJ (14 caracteres).";
    else if (!documentoValido(form.documento)) erros.documento = tipo === "PF" ? "CPF inválido." : "CNPJ inválido.";
    if (!form.nomeRazao.trim()) erros.nomeRazao = tipo === "PJ" ? "Razão Social é obrigatória." : "Nome completo é obrigatório.";
    return erros;
  };

  // ── Salvar (criar ou editar)
  const handleSalvar = async () => {
    if (processando) return;
    const erros = validarForm();
    if (Object.keys(erros).length > 0) {
      setErrosForm(erros);
      cnpjInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setErrosForm({});

    const corpo = {
      tipoPessoa: tipoDoForm(form),
      documento: numeros(form.documento),
      ...camposDocumentoEstrangeiro(form),
      nomeRazao: maiusculas(form.nomeRazao.trim()),
      apelido: maiusculas(form.apelido.trim()),
      ativo: form.ativo,
      observacoes: maiusculas(form.observacoes.trim()),
    };

    setProcessando(true);
    try {
      await chamarApi(editandoId ? `/${editandoId}` : "", { method: editandoId ? "PUT" : "POST", body: JSON.stringify(corpo) });
      mostrarSnack(editandoId ? "Sócio atualizado!" : "Sócio cadastrado com sucesso!");
      await carregarSocios();
      handleCancelarForm();
    } catch (erro) {
      mostrarSnack(erro.message, "error");
    } finally {
      setProcessando(false);
    }
  };

  // ── Excluir
  const handleConfirmarDelete = (id) => {
    document.activeElement?.blur();
    setConfirmDelete({ aberto: true, id });
  };

  const handleExcluirConfirmado = async () => {
    const { id } = confirmDelete;
    setConfirmDelete({ aberto: false, id: null });
    try {
      await chamarApi(`/${id}`, { method: "DELETE" });
      if (editandoId === id) handleCancelarForm();
      await carregarSocios();
      mostrarSnack("Sócio excluído.", "warning");
    } catch (erro) {
      mostrarSnack(erro.message, "error");
    }
  };

  if (!podeVisualizar) {
    return (
      <ThemeProvider theme={muiTheme}>
        <Box sx={{ color: "var(--ig-t-ffffff)" }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Sócios
          </Typography>
          <Alert severity="warning">Você não possui permissão para acessar esta tela.</Alert>
        </Box>
      </ThemeProvider>
    );
  }

  const tipoAtual = tipoDoForm(form);
  const tipoPeloDocumento = tipoPorDocumento(form.documento);

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "var(--ig-t-ffffff)" }}>
        {/* Título */}
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Sócios
        </Typography>

        {/* ── Filtro ─────────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Filtrar Sócio"
            value={inputFiltro}
            onChange={(e) => setInputFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePesquisar();
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "var(--ig-t-9ca3af)", fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": { backgroundColor: "transparent", color: "var(--ig-t-ffffff)" },
              "& input::placeholder": { color: "var(--ig-t-ffffff)", opacity: 1, fontWeight: 500, fontSize: "1rem" },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            type="button"
            onClick={handleLimparFiltro}
            sx={{
              flexShrink: 0,
              width: "auto",
              height: 40,
              color: "#2196f3",
              borderColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": { borderColor: "#42a5f5", backgroundColor: "rgba(33, 150, 243, 0.08)" },
            }}
          >
            Limpar Filtro
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCopiar}
            sx={{ height: 40, backgroundColor: "#2196f3", fontWeight: 600, textTransform: "none", "&:hover": { backgroundColor: "#1976d2" } }}
          >
            <i className="fas fa-copy" style={{ marginRight: 6 }} />
            Copiar
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCSV}
            sx={{ height: 40, backgroundColor: "#2196f3", fontWeight: 600, textTransform: "none", "&:hover": { backgroundColor: "#1976d2" } }}
          >
            CSV
          </Button>
        </Box>

        {/* ── Minimizar grid ───────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1 }}>
          {!gridMinimizado ? (
            <button
              type="button"
              onClick={() => setGridMinimizado(true)}
              style={{
                border: "1px solid #d32f2f",
                color: "#ff4c4c",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setGridMinimizado(false)}
              style={{
                border: "1px solid #1998a8",
                color: "#1998a8",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↗ Expandir
            </button>
          )}
        </Box>

        {/* ── Grid ─────────────────────────────────────────────────────────── */}
        <Collapse in={!gridMinimizado}>
          <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: "transparent", boxShadow: "none" }}>
            <Table size="small" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={estiloCabecalho}>Ações</TableCell>
                  <TableCell align="center" sx={estiloCabecalho}>
                    Tipo
                  </TableCell>
                  <TableCell sx={{ ...estiloCabecalho, width: "100%" }}>Nome / Razão Social</TableCell>
                  <TableCell sx={estiloCabecalho}>CPF / CNPJ</TableCell>
                  <TableCell sx={estiloCabecalho}>Apelido</TableCell>
                  <TableCell align="center" sx={estiloCabecalho}>
                    Situação
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sociosPagina.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ color: "var(--ig-t-888888)", py: 3, borderBottom: "1px solid var(--ig-b-444444)" }}>
                      {carregando ? "Carregando sócios..." : "Nenhum sócio encontrado."}
                    </TableCell>
                  </TableRow>
                ) : (
                  sociosPagina.map((socio) => (
                    <TableRow
                      key={socio.id}
                      sx={{
                        "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" },
                        backgroundColor: editandoId === socio.id ? "rgba(25,152,168,0.08)" : "transparent",
                      }}
                    >
                      <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                        <Tooltip title="Visualizar / Editar">
                          <IconButton size="small" onClick={() => handleVisualizar(socio)} sx={estiloBotaoAcao("#1998a8", "#1565c0")}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {podeExcluir && (
                          <Tooltip title="Excluir sócio">
                            <IconButton size="small" onClick={() => handleConfirmarDelete(socio.id)} sx={estiloBotaoAcao("#d32f2f", "#c62828")}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                        <Tooltip title={labelTipo(socio.tipoPessoa)}>
                          <Chip
                            size="small"
                            label={socio.tipoPessoa}
                            sx={{ backgroundColor: socio.tipoPessoa === "PJ" ? "#1565c0" : "#1998a8", color: "#fff", fontWeight: "bold" }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)" }}>{socio.nomeRazao}</TableCell>
                      <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                        {socio.documento || descreverDocumentoEstrangeiro(socio) || "—"}
                      </TableCell>
                      <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-cccccc)", whiteSpace: "nowrap" }}>
                        {socio.apelido || "—"}
                      </TableCell>
                      <TableCell align="center" sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                        <Chip
                          size="small"
                          label={socio.ativo ? "Ativo" : "Inativo"}
                          sx={{ backgroundColor: socio.ativo ? "#2e7d32" : "#555", color: "#fff", fontWeight: "bold" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              component="div"
              count={sociosFiltrados.length}
              page={pagina}
              onPageChange={(_, p) => setPagina(p)}
              rowsPerPage={porPagina}
              onRowsPerPageChange={(e) => {
                setPorPagina(parseInt(e.target.value, 10));
                setPagina(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Sócios por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              sx={{
                color: "var(--ig-t-ffffff)",
                borderTop: "1px solid var(--ig-b-444444)",
                "& .MuiTablePagination-selectIcon": { color: "var(--ig-t-ffffff)" },
                "& .MuiTablePagination-actions button": { color: "var(--ig-t-ffffff)", width: "auto" },
                "& .MuiTablePagination-actions": { display: "flex", alignItems: "center" },
              }}
            />
          </TableContainer>
        </Collapse>

        {/* ── Formulário inferior (visualizar / criar / editar) ────────────── */}
        {(podeCadastrar || (podeVisualizar && editandoId)) && (
          <Paper sx={{ p: 3, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, boxShadow: "0 0 20px rgba(0,0,0,0.4)" }}>
            <Typography variant="h6" sx={{ mb: 2, color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
              {editandoId && somenteLeitura ? "Visualizar Sócio" : editandoId ? "Editar Sócio" : "Novo Sócio"}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label={form.estrangeiro ? "CPF / CNPJ (opcional)" : "CPF / CNPJ*"}
                  size="small"
                  inputRef={cnpjInputRef}
                  value={form.documento}
                  onChange={(e) => {
                    const valor = formatarDocumento(e.target.value);
                    limparConsultaCnpj();
                    atualizarCampo("documento", valor);
                    if (numeros(valor).length === 14) consultarCnpj(valor);
                  }}
                  error={!!errosForm.documento}
                  helperText={errosForm.documento || mensagemCnpj || " "}
                  slotProps={{
                    input: {
                      readOnly: somenteLeitura,
                      endAdornment: consultandoCnpj ? (
                        <InputAdornment position="end">
                          <CircularProgress size={18} />
                        </InputAdornment>
                      ) : null,
                    },
                    htmlInput: { maxLength: 18, inputMode: "text" },
                  }}
                  sx={{ flex: 1, minWidth: 220, ...estiloCampo(somenteLeitura, errosForm.documento) }}
                />

                <Box sx={{ display: "flex", alignItems: "center", pb: 2.5 }}>
                  <FlagEstrangeiro valor={form} onChange={atualizarCampo} somenteLeitura={somenteLeitura} rotulo="Sócio estrangeiro" />
                </Box>

                {form.estrangeiro && !tipoPeloDocumento ? (
                  <TextField
                    select
                    label="Tipo de Pessoa*"
                    size="small"
                    value={form.tipoPessoaManual || ""}
                    onChange={(e) => atualizarCampo("tipoPessoaManual", e.target.value)}
                    error={!!errosForm.tipoPessoaManual}
                    helperText={errosForm.tipoPessoaManual || "Estrangeiro sem CPF/CNPJ: escolha o tipo."}
                    slotProps={{ input: { readOnly: somenteLeitura } }}
                    sx={{ flex: 1, minWidth: 180, ...estiloCampo(somenteLeitura, errosForm.tipoPessoaManual) }}
                  >
                    <MenuItem value="PF">Pessoa Física</MenuItem>
                    <MenuItem value="PJ">Pessoa Jurídica</MenuItem>
                  </TextField>
                ) : (
                  <TextField
                    label="Tipo de Pessoa"
                    size="small"
                    value={labelTipo(tipoAtual)}
                    helperText="Identificado pelo documento."
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ flex: 1, minWidth: 180, ...estiloCampo(true, false) }}
                  />
                )}

                <TextField
                  label={tipoAtual === "PJ" ? "Razão Social*" : "Nome Completo*"}
                  size="small"
                  value={form.nomeRazao}
                  onChange={(e) => atualizarCampo("nomeRazao", maiusculas(e.target.value))}
                  error={!!errosForm.nomeRazao}
                  helperText={errosForm.nomeRazao || (tipoAtual === "PJ" && !somenteLeitura ? "Preenchida pela consulta do CNPJ; pode ser ajustada." : " ")}
                  slotProps={{ input: { readOnly: somenteLeitura }, htmlInput: { maxLength: 255 } }}
                  sx={{ flex: 3, minWidth: 280, ...estiloCampo(somenteLeitura, errosForm.nomeRazao) }}
                />
              </Box>

              <DocumentoEstrangeiroCampos
                obrigatorio={!numeros(form.documento)}
                mostrarFlag={false}
                valor={form}
                onChange={atualizarCampo}
                somenteLeitura={somenteLeitura}
                erros={errosForm}
                campoSx={(campo) => estiloCampo(somenteLeitura, errosForm[campo])}
              />

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  label="Apelido"
                  size="small"
                  value={form.apelido}
                  onChange={(e) => atualizarCampo("apelido", maiusculas(e.target.value))}
                  slotProps={{ input: { readOnly: somenteLeitura }, htmlInput: { maxLength: 100 } }}
                  sx={{ flex: 3, minWidth: 220, ...estiloCampo(somenteLeitura, false) }}
                />

                <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
                  <InputLabel sx={{ color: "var(--ig-t-ffffff)", fontWeight: "bold" }}>Situação*</InputLabel>
                  <Select
                    label="Situação*"
                    value={form.ativo ? "ATIVO" : "INATIVO"}
                    onChange={(e) => atualizarCampo("ativo", e.target.value === "ATIVO")}
                    inputProps={{ readOnly: somenteLeitura }}
                    sx={estiloSelect(somenteLeitura)}
                  >
                    <MenuItem value="ATIVO">Ativo</MenuItem>
                    <MenuItem value="INATIVO">Inativo</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TextField
                label="Observações"
                size="small"
                multiline
                minRows={2}
                value={form.observacoes}
                onChange={(e) => atualizarCampo("observacoes", maiusculas(e.target.value))}
                slotProps={{ input: { readOnly: somenteLeitura } }}
                sx={estiloCampo(somenteLeitura, false)}
              />

              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                {/* Modo visualização */}
                {editandoId && somenteLeitura && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleCancelarForm}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",
                        "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                        fontWeight: "bold",
                        px: 3,
                      }}
                    >
                      Fechar
                    </Button>
                    {podeCadastrar && (
                      <Button
                        variant="contained"
                        startIcon={<FaEdit />}
                        onClick={handleHabilitarEdicao}
                        sx={{ width: "auto", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold", py: 1, px: 3 }}
                      >
                        Editar
                      </Button>
                    )}
                    {podeExcluir && (
                      <Button
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleConfirmarDelete(editandoId)}
                        sx={{ width: "auto", backgroundColor: "#d32f2f", "&:hover": { backgroundColor: "#c62828" }, fontWeight: "bold", py: 1, px: 3 }}
                      >
                        Excluir
                      </Button>
                    )}
                  </>
                )}
                {/* Modo edição / criação */}
                {(!editandoId || !somenteLeitura) && (
                  <>
                    {editandoId && (
                      <Button
                        variant="outlined"
                        startIcon={<FaTimes />}
                        onClick={handleCancelarForm}
                        sx={{
                          width: "auto",
                          borderColor: "var(--ig-b-555555)",
                          color: "var(--ig-t-cccccc)",
                          "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                          fontWeight: "bold",
                          px: 3,
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                    {podeCadastrar && (
                      <Button
                        variant="contained"
                        startIcon={<FaSave />}
                        onClick={handleSalvar}
                        disabled={processando}
                        sx={{ width: "auto", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" }, fontWeight: "bold", py: 1, px: 4 }}
                      >
                        {editandoId ? "Atualizar" : "Cadastrar"}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* ── Dialog: Confirmação de exclusão ──────────────────────────────── */}
      <Dialog open={confirmDelete.aberto} onClose={() => setConfirmDelete({ aberto: false, id: null })}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "var(--ig-t-cccccc)" }}>Deseja excluir este sócio? Esta ação não pode ser desfeita.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmDelete({ aberto: false, id: null })}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleExcluirConfirmado}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.aberto}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, aberto: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, aberto: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
